'use client'

import React from 'react'
import type { SectionHeaderBlock as SectionHeaderBlockProps } from '@/payload-types'
import { SubHeading } from './SubHeading'
import { getSectionHeadingLevels } from './getSectionHeadingLevels'
import { getBlockPadding } from '@/utilities/getBlockPadding'

type Props = SectionHeaderBlockProps & {
  disableInnerContainer?: boolean
  hasHero?: boolean
  isFirstSectionHeader?: boolean
}

export const SectionHeaderBlock: React.FC<Props> = ({
  subHeadingType,
  subHeading,
  subHeadingLogo,
  heading,
  description,
  description2,
  disableInnerContainer: _disableInnerContainer,
  hasHero = true,
  isFirstSectionHeader = false,
  paddingTop,
  paddingBottom,
}) => {
  // Get padding classes based on layout options
  // SectionHeader defaults to no bottom padding (pb-0) to preserve existing behavior
  const paddingClasses = getBlockPadding({
    paddingTop: paddingTop ?? true,
    paddingBottom: paddingBottom ?? false,
  })
  // Determine if we're showing a logo or text for subheading
  const showLogo = subHeadingType === 'logo' && subHeadingLogo
  const showTextSubHeading = subHeadingType !== 'logo' && subHeading
  const hasSubHeading = showLogo || showTextSubHeading

  const { headingLevel, eyebrowLevel } = getSectionHeadingLevels({ hasHero, isFirstSectionHeader, hasHeading: Boolean(heading) })

  // Determine if we have descriptions to show
  const hasDescription = description && description.trim() !== ''
  const hasDescription2 = description2 && description2.trim() !== ''
  const hasAnyDescription = hasDescription || hasDescription2

  return (
    <div className={`box-border content-stretch flex flex-col items-start justify-start relative w-full ${paddingClasses}`}>
      <div className="content-stretch flex flex-col gap-7 lg:gap-16 items-start justify-start relative shrink-0 w-full">
        {/* Sub Heading with Line */}
        {hasSubHeading && (
          <SubHeading
            text={showTextSubHeading ? subHeading : undefined}
            logo={showLogo ? subHeadingLogo : undefined}
            headingLevel={eyebrowLevel}
          />
        )}

        {/* Heading and Descriptions container - only render if there's content */}
        {(heading || hasAnyDescription) && (
          <div className={`content-stretch flex flex-col gap-4 items-start justify-start relative shrink-0 w-full ${hasAnyDescription ? 'lg:flex-row' : ''} lg:items-start lg:justify-start lg:leading-[0]`}>
            {/* Heading */}
            {heading && (
              <div className={`display-small relative shrink-0 w-full lg:display-large ${hasAnyDescription ? 'lg:w-1/2' : ''} min-w-0 whitespace-pre-line`}>
                {headingLevel === 'h1' && <h1 className="mb-0">{heading}</h1>}
                {headingLevel === 'h2' && <h2 className="mb-0">{heading}</h2>}
              </div>
            )}

            {/* Descriptions */}
            {hasAnyDescription && (
              <div className="body-medium text-black/70 relative shrink-0 w-full lg:body-large flex-shrink-0 lg:w-1/2 flex flex-col gap-4">
                {hasDescription && (
                  <p>{description}</p>
                )}
                {hasDescription2 && (
                  <p>{description2}</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}